import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { Modal, Footer } from 'tio-ui/components/modal';
import { Input, Textarea } from 'tio-ui/components/forms';
import { VStack } from 'tio-ui/components/layout';
import Alert from 'tio-ui/components/alert';
import { not, or } from 'tio-ui/utilities';
import { dropTask } from 'ember-concurrency';
import { divide } from 'tio-ui/math';
import inputmask from '../../../modifiers/inputmask.ts';
import LimitsSummary from '../../tuition-assistance/limits-summary.gts';
import SelectTextArea from '../../tio/select-text-area.gts';
import TuitionAssistanceApplicationDetailsCoursesApprovalWaiverChecklist from '../../tuition-assistance/application-details/courses-approval-waiver-checklist.gts';
import TioConfirmAlert from '../../tio/confirm-alert.gts';
import TioErrorMessages from '../../tio/error-messages.gts';
import { getSingleValueForTasField } from '../../../utils/tuition-assistance/fields.ts';
import { doEmployeesExistInApproverList } from '../../../utils/tuition-assistance/approvers.ts';
import CalculatePrePercentageValue from '../../../utils/tuition-assistance/calculate-pre-percentage-value.ts';
import type Owner from '@ember/owner';
import type Store from '@ember-data/store';
import type RouterService from '@ember/routing/router-service';
import type TasApplicationModel from '../../../models/tas-application';
import type TuitionAssistanceService from '../../../services/tuition-assistance';
import type FeaturesService from '../../../services/features';

/**
 * Approval Modal Component
 *
 * Focused modal for approving TAS applications. Handles:
 * - Course approval (PENDING_COURSES_APPROVAL)
 * - Evidence approval (PENDING_EVIDENCE_APPROVAL)
 * - Approved amount editing with validation
 * - Course Cash dual totals for prepaid programs
 * - Limits validation integration
 * - Waiver checklist for skipping evidence requirements
 * - Auto-evidence approval trigger
 *
 * Triggered from: approval-page > approval-footer > Approve button
 */
interface ApproveModalSignature {
  Element: HTMLDivElement;
  Args: {
    /** Whether modal is open */
    show: boolean;
    /** Callback to close modal */
    onClose: () => void;
    /** TAS Application to approve */
    application: TasApplicationModel;
  };
}

class ApproveModal extends Component<ApproveModalSignature> {
  @service declare store: Store;
  @service declare tuitionAssistance: TuitionAssistanceService;
  @service declare router: RouterService;
  @service declare sessionContext: unknown;
  @service declare features: FeaturesService;

  // Form state
  @tracked approvedTotal = 0;
  @tracked cardFinalApprovedTotal = 0;
  @tracked comment = '';
  @tracked submissionError: string | null = null;

  // Pre-percentage values (for percentage-based calculations)
  @tracked prePercentageTotal = 0;
  @tracked prePercentageCourseCashTotal = 0;

  // Waiver state
  @tracked hasConfirmedEvidenceAttachmentWaiver = false;
  @tracked hasConfirmedEvidenceApprovalWaiver = false;
  @tracked hasRespondedToAllChecklistItems = false;

  // UI state
  @tracked isProcessing = false;
  @tracked showConfirmApproverOverridePrompt = false;

  constructor(owner: Owner, args: ApproveModalSignature['Args']) {
    super(owner, args);
    // Initialize on first load if modal is shown
    if (args.show) {
      this.initializeFormDefaults();
    }
  }

  /**
   * Initialize form with default values when modal opens
   * Call this whenever the modal is shown to reset state
   */
  initializeFormDefaults() {
    // TODO: Auto-populate approved total based on calculation strategy
    // For now, initialize with requested amount or existing approved amount
    const { application } = this.args;
    const reimbursementPercentage = this.reimbursementPercentage;

    // Initialize Course Cash amount if applicable
    if (this.isCourseCash) {
      // Use cardFinalApprovedTotal if it exists, otherwise fall back to cardPreApprovedTotal
      const cardTotal = application.cardFinalApprovedTotal ?? application.cardPreApprovedTotal ?? 0;
      this.cardFinalApprovedTotal = typeof cardTotal === 'number' ? cardTotal : 0;

      // For Course Cash applications, "All Other Expenses" should start at 0
      // since the course cash total is tracked separately
      const existingApprovedTotal = application.approvedTotal || 0;
      this.approvedTotal = existingApprovedTotal > 0 ? existingApprovedTotal : 0;

      // Initialize pre-percentage values for percentage-based calculations
      if (
        this.isCalculatedApplicationTotal &&
        reimbursementPercentage &&
        reimbursementPercentage > 0
      ) {
        this.prePercentageCourseCashTotal = CalculatePrePercentageValue(
          this.cardFinalApprovedTotal,
          reimbursementPercentage
        );
        this.prePercentageTotal = CalculatePrePercentageValue(
          this.approvedTotal,
          reimbursementPercentage
        );
      }
    } else {
      // For non-Course Cash applications, use approved total or requested total
      const existingApprovedTotal = application.approvedTotal || 0;
      this.approvedTotal =
        existingApprovedTotal > 0 ? existingApprovedTotal : application.requestedTotal || 0;

      // Initialize pre-percentage value for percentage-based calculations
      if (this.isCalculatedApplicationTotal && reimbursementPercentage) {
        this.prePercentageTotal = CalculatePrePercentageValue(
          this.approvedTotal,
          reimbursementPercentage
        );
      }
    }

    // Reset other form state
    this.comment = '';
    this.submissionError = null;
    this.hasConfirmedEvidenceAttachmentWaiver = false;
    this.hasConfirmedEvidenceApprovalWaiver = false;
  }

  /**
   * Reset form state to defaults
   */
  resetFormState() {
    this.approvedTotal = 0;
    this.cardFinalApprovedTotal = 0;
    this.prePercentageTotal = 0;
    this.prePercentageCourseCashTotal = 0;
    this.comment = '';
    this.submissionError = null;
    this.hasConfirmedEvidenceAttachmentWaiver = false;
    this.hasConfirmedEvidenceApprovalWaiver = false;
    this.hasRespondedToAllChecklistItems = false;
    this.showConfirmApproverOverridePrompt = false;
    this.isProcessing = false;
  }

  /**
   * Determine if application is in PENDING_COURSES_APPROVAL state
   */
  get isPendingCoursesApproval() {
    return this.args.application.state === 'TAS.ApplicationState.PENDING_COURSES_APPROVAL';
  }

  /**
   * Determine if application is in PENDING_EVIDENCE_APPROVAL state
   */
  get isPendingEvidenceApproval() {
    return this.args.application.state === 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL';
  }

  /**
   * Check if application uses Course Cash payment method
   */
  get isCourseCash() {
    return this.args.application.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH';
  }

  /**
   * Check if application uses percentage-based or GPA-based calculation
   * This determines whether to show the "X% reimbursement of" inputs
   */
  get isCalculatedApplicationTotal() {
    const template = this.args.application.tasProgramInstance?.tasProgramTemplate;
    if (!template) return false;
    return template.calculateTotalByPercent || template.calculateTotalByGPA;
  }

  /**
   * Get the reimbursement percentage from the program template
   */
  get reimbursementPercentage() {
    return (
      this.args.application.tasProgramInstance?.tasProgramTemplate?.reimbursementPercentage ?? 0
    );
  }

  /**
   * Determine which adapter method to call based on state
   */
  get approvalAdapterMethod() {
    if (this.isPendingCoursesApproval) {
      return 'approveCourses';
    } else if (this.isPendingEvidenceApproval) {
      return 'approveCourseEvidence';
    }
    return 'approveCourses'; // Default fallback
  }

  /**
   * Determine which field to update (PRE_APPROVED_TOTAL or FINAL_APPROVED_TOTAL)
   */
  get fieldToUpdate(): 'PRE_APPROVED_TOTAL' | 'FINAL_APPROVED_TOTAL' {
    // Before pre-approval states use PRE_APPROVED_TOTAL
    const beforePreApprovalStates = [
      'TAS.ApplicationState.CREATED',
      'TAS.ApplicationState.DEFINE_COURSES',
      'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
      'TAS.ApplicationState.COURSES_APPROVED',
      'TAS.ApplicationState.COURSES_NOT_APPROVED',
      'TAS.ApplicationState.COURSES_REJECTED',
    ];

    return beforePreApprovalStates.includes(this.args.application.state)
      ? 'PRE_APPROVED_TOTAL'
      : 'FINAL_APPROVED_TOTAL';
  }

  /**
   * Check if Course Cash field should be shown
   * Only show for Course Cash programs in PENDING_COURSES_APPROVAL
   */
  get showCourseCashField() {
    return this.isCourseCash && this.isPendingCoursesApproval;
  }

  /**
   * Get canned response notes for approval action
   * Fetches pre-defined response text from company comments
   */
  get tasResponseNotes() {
    const partnerComments = this.args.application.tasProgramInstance.employee.company.comments;
    return partnerComments
      .filter((comment) => comment.isTasApproval)
      .map((comment) => comment.tasApprovalNote);
  }

  /**
   * Check if waiver checklist should be displayed
   * Only show for PENDING_COURSES_APPROVAL when waiver is allowed
   */
  get canSkipFinalApprovalOrAttachmentsWithChecklist() {
    return (
      this.isPendingCoursesApproval &&
      getSingleValueForTasField(
        'ALLOW_WAIVER_OF_EVIDENCE_APPROVAL',
        this.args.application.tasProgramInstance.tasProgramTemplate.fields
      ) === true
    );
  }

  /**
   * Check if current user is the expected approver
   * Used to show out-of-sequence warning
   */
  get isExpectedApprover() {
    const expectedApproverEmployeeIds = this.args.application.currentExpectedApproverEmployeeIds;
    // @ts-expect-error session context is pulled from wrong place
    const employees = this.sessionContext.user?.person?.employees || [];
    return doEmployeesExistInApproverList(employees, expectedApproverEmployeeIds);
  }

  /**
   * Check if employee has applications in repayment status
   */
  get employeeHasAppsInRepayment() {
    return !!this.appsInRepayment.length;
  }

  /**
   * Get list of application IDs that are in repayment
   */
  get appsInRepayment() {
    // This would need to be fetched from the employee's applications
    // For now, return empty array - implementation depends on data availability
    return [];
  }

  /**
   * Get the appropriate route for returning to the approval queue
   * Routes differ based on consuming app
   */
  get approvalQueueRoute() {
    if (this.features.isEmployeeApp) {
      return 'authenticated.admin.tas.approval-queue';
    }
    // Default for tio-admin
    return 'authenticated.tas.approval-queue';
  }

  /**
   * Check if TAS participant is inactive
   */
  get isTasParticipantInactive() {
    return !this.args.application.tasProgramInstance.employee?.tasParticipant?.isActive;
  }

  /**
   * Validate if approval can be submitted
   */
  get canSubmitApproval() {
    // For Course Cash programs, cardFinalApprovedTotal must be > 0 (approvedTotal can be 0)
    // For non-Course Cash programs, approvedTotal must be > 0
    const hasValidTotal = this.isCourseCash
      ? this.cardFinalApprovedTotal > 0
      : this.approvedTotal > 0;

    // If waiver checklist is shown, require all items to be responded to
    if (this.canSkipFinalApprovalOrAttachmentsWithChecklist) {
      return (
        !this.isProcessing &&
        hasValidTotal &&
        this.comment.length > 0 &&
        this.hasRespondedToAllChecklistItems
      );
    }

    return (
      !this.isProcessing && hasValidTotal && this.comment.length > 0
      // Limits validation is handled by LimitsSummary component
      // which will disable the button via isDisabledSubmitForApprover
    );
  }

  /**
   * Update approved total input (direct input, not percentage-based)
   */
  updateApprovedTotal = (value: string | number) => {
    // inputmask with autoUnmask returns numeric value, multiply by 100 to get cents
    this.approvedTotal = Math.round(Number(value) * 100);
    // Refresh limits validation when amount changes
    this.tuitionAssistance.refreshActiveLimitsSummaryComponents();
  };

  /**
   * Update Course Cash total input (direct input, not percentage-based)
   */
  updateCardFinalApprovedTotal = (value: string | number) => {
    // inputmask with autoUnmask returns numeric value, multiply by 100 to get cents
    this.cardFinalApprovedTotal = Math.round(Number(value) * 100);
    // Refresh limits validation when amount changes
    this.tuitionAssistance.refreshActiveLimitsSummaryComponents();
  };

  /**
   * Update approved total via pre-percentage input (percentage-based calculation)
   * Takes the pre-percentage amount and calculates the final approved amount
   */
  updateApprovedTotalFromPrePercentage = (value: string | number) => {
    const numericValue = Number(value);
    const reimbursementPercentage = this.reimbursementPercentage;

    if (isNaN(numericValue) || !reimbursementPercentage || isNaN(reimbursementPercentage)) {
      this.approvedTotal = 0;
      this.prePercentageTotal = 0;
      return;
    }

    const updatedPrePercentageTotal = Math.round(numericValue * 100);
    this.prePercentageTotal = updatedPrePercentageTotal;
    this.approvedTotal = Math.round(updatedPrePercentageTotal * (reimbursementPercentage / 100));
    this.tuitionAssistance.refreshActiveLimitsSummaryComponents();
  };

  /**
   * Update Course Cash total via pre-percentage input (percentage-based calculation)
   */
  updateCourseCashFromPrePercentage = (value: string | number) => {
    const numericValue = Number(value);
    const reimbursementPercentage = this.reimbursementPercentage;

    if (isNaN(numericValue) || !reimbursementPercentage || isNaN(reimbursementPercentage)) {
      this.cardFinalApprovedTotal = 0;
      this.prePercentageCourseCashTotal = 0;
      return;
    }

    const updatedPrePercentageTotal = Math.round(numericValue * 100);
    this.prePercentageCourseCashTotal = updatedPrePercentageTotal;
    this.cardFinalApprovedTotal = Math.round(
      updatedPrePercentageTotal * (reimbursementPercentage / 100)
    );
    this.tuitionAssistance.refreshActiveLimitsSummaryComponents();
  };

  /**
   * Update comment textarea
   */
  updateComment = (value: string) => {
    this.comment = value;
  };

  /**
   * Handle waiver checklist changes
   */
  didChangeApprovalWaiverChecklist = ({
    hasConfirmedEvidenceApprovalWaiver,
    hasConfirmedEvidenceAttachmentWaiver,
    hasRespondedToAllChecklistItems,
  }: {
    hasConfirmedEvidenceApprovalWaiver: boolean;
    hasConfirmedEvidenceAttachmentWaiver: boolean;
    hasRespondedToAllChecklistItems: boolean;
  }) => {
    this.hasConfirmedEvidenceApprovalWaiver = hasConfirmedEvidenceApprovalWaiver;
    this.hasConfirmedEvidenceAttachmentWaiver = hasConfirmedEvidenceAttachmentWaiver;
    this.hasRespondedToAllChecklistItems = hasRespondedToAllChecklistItems;
  };

  /**
   * Handle modal close
   */
  handleClose = () => {
    if (!this.isProcessing) {
      this.resetFormState();
      this.args.onClose();
    }
  };

  /**
   * Apply evidence waivers if applicable
   * This sets flags to skip evidence requirements based on waiver checklist
   */
  waiveRequirementsIfApplicable = async (application: TasApplicationModel) => {
    if (
      !this.canSkipFinalApprovalOrAttachmentsWithChecklist ||
      // Evidence approval can only be waived if attachment requirement is waived also
      !this.hasConfirmedEvidenceAttachmentWaiver
    ) {
      return;
    }

    const adapter = this.store.adapterFor('tas-application');

    if (this.hasConfirmedEvidenceApprovalWaiver) {
      // This endpoint sets WAIVE_EVIDENCE_ATTACHMENT_REQUIREMENT and
      // WAIVE_EVIDENCE_APPROVAL_REQUIREMENT to true, overriding approval summaries
      // to enable skipping subsequent approval steps
      return adapter.waiveEvidenceApproval(application);
    } else {
      // If not waiving full approval, still waive the attachment requirement
      const { setSingleValueForTasField } = await import(
        '../../../utils/tuition-assistance/fields.ts'
      );
      setSingleValueForTasField('WAIVE_EVIDENCE_ATTACHMENT_REQUIREMENT', true, application.fields);
      return application.save();
    }
  };

  /**
   * Attempt to submit approval - checks if user is expected approver first
   */
  attemptSubmitApproval = () => {
    if (!this.isExpectedApprover) {
      this.showConfirmApproverOverridePrompt = true;
    } else {
      this.submitApprovalTask.perform();
    }
  };

  /**
   * Cancel the approver override prompt
   */
  cancelApproverOverride = () => {
    this.showConfirmApproverOverridePrompt = false;
  };

  /**
   * Main approval submission task
   */
  submitApprovalTask = dropTask(async () => {
    this.showConfirmApproverOverridePrompt = false;
    this.isProcessing = true;
    this.submissionError = null;

    try {
      const { application } = this.args;

      const adapter = this.store.adapterFor('tas-application');

      // Step 1: Update approved total(s)
      const additionalFields: Record<string, unknown> = {};

      // For Course Cash programs, include card total
      if (this.showCourseCashField) {
        additionalFields['CARD_FINAL_APPROVED_TOTAL'] = this.cardFinalApprovedTotal;
      }

      await adapter.updateApprovedTotal(
        application,
        this.approvedTotal,
        this.comment,
        additionalFields
      );

      // Step 2: Apply waivers if attachment waiver is confirmed
      if (this.hasConfirmedEvidenceAttachmentWaiver) {
        await this.waiveRequirementsIfApplicable(application);
      }

      // Step 3: Call main approval method
      await adapter[this.approvalAdapterMethod](application, this.comment);

      // Step 4: Trigger auto-evidence if full approval waiver checked
      if (this.hasConfirmedEvidenceApprovalWaiver) {
        await this.tuitionAssistance.triggerAutoEvidenceApprovalForApplication(
          application,
          this.comment
        );
      }

      // Navigate back to approval queue
      this.router.transitionTo(this.approvalQueueRoute);
    } catch (error) {
      console.error('Approval submission failed:', error);
      this.submissionError =
        error instanceof Error ? error.message : 'An error occurred during approval';
    } finally {
      this.isProcessing = false;
    }
  });

  <template>
    <Modal
      @isOpen={{@show}}
      @onClose={{this.handleClose}}
      @backdrop="faded"
      class="min-w-[600px]"
      data-test-approve-modal
      as |m|
    >
      <m.Header>
        Application Approval
      </m.Header>

      <m.Body>
        <LimitsSummary
          @application={{@application}}
          @employee={{@application.tasProgramInstance.employee}}
          @approvedTotalToValidate={{this.approvedTotal}}
          @approvedCourseCashTotalToValidate={{this.cardFinalApprovedTotal}}
          as |Summary|
        >
          <VStack>
            {{! Error Display }}
            {{#if this.submissionError}}
              <Alert @intent="danger" data-test-approve-modal-error>
                {{this.submissionError}}
              </Alert>
            {{/if}}

            {{! Limits validation alert }}
            <Summary.LimitsAlertForApproverIfApplicable class="text-sm flex" />

            {{! Waiver Checklist Section - Must be completed first }}
            {{#if this.canSkipFinalApprovalOrAttachmentsWithChecklist}}
              <div class="">
                <h3 class="text-sm font-medium">Bypass evidence approval step</h3>
                <TuitionAssistanceApplicationDetailsCoursesApprovalWaiverChecklist
                  @programTemplate={{@application.tasProgramInstance.tasProgramTemplate}}
                  @didChange={{this.didChangeApprovalWaiverChecklist}}
                />
              </div>
            {{/if}}

            {{! Request Summary Section }}
            {{!-- <Section>
              <:header>
                {{t "tuition_assistance.application_details.approval_summary"}}
              </:header>
              <:body>
                <DefinitionList as |options|>
                  {{! Course breakdown }}
                  {{#each @application.activeCourses as |course index|}}
                    <div class={{options.wrappingClass}}>
                      <options.dt class="truncate">
                        {{or
                          (or course.courseName course.courseNumber)
                          (concat "Expense " (add index 1))
                        }}
                      </options.dt>
                      <options.dd>
                        {{safeFormatNumber
                          (divide course.courseTotal 100)
                          style="currency"
                          currency="USD"
                        }}
                      </options.dd>
                    </div>
                  {{/each}}

                  {{! Scholarships if applicable }}
                  {{#if @application.includesScholarships}}
                    <div class={{options.wrappingClass}}>
                      <options.dt>
                        {{t "tuition_assistance.application_details.scholarships_received"}}
                      </options.dt>
                      <options.dd>
                        {{safeFormatNumber
                          (divide (subtract 0 @application.scholarshipsTotal) 100)
                          style="currency"
                          currency="USD"
                        }}
                      </options.dd>
                    </div>
                  {{/if}}

                  {{! Requested Total }}
                  <div class={{options.wrappingClass}}>
                    <options.dt>
                      {{t "tuition_assistance.application_details.requested_reimbursement"}}
                    </options.dt>
                    <options.dd>
                      {{safeFormatNumber
                        (divide @application.requestedTotal 100)
                        style="currency"
                        currency="USD"
                      }}
                    </options.dd>
                  </div>
                </DefinitionList>
              </:body>
            </Section> --}}

            {{! Approved Amount Section }}
            <div class="space-y-4">
              {{#if this.isCalculatedApplicationTotal}}
                {{! Percentage-based calculation UI }}
                {{#if this.showCourseCashField}}
                  {{! Course Cash with percentage }}
                  <div class="grid grid-cols-2 gap-2 items-center">
                    <label class="font-semibold">
                      {{this.reimbursementPercentage}}% reimbursement of
                    </label>
                    <Input
                      @value={{String (divide (or this.prePercentageCourseCashTotal 0) 100)}}
                      @onChange={{this.updateCourseCashFromPrePercentage}}
                      {{inputmask
                        alias="currency"
                        prefix="$"
                        unmaskAsNumber=true
                        digits="2"
                        autoUnmask=true
                      }}
                      @description="Course Cash - Tuition"
                      data-test-pre-percentage-cc-total
                    />

                    <label class="font-semibold">Approved Total</label>
                    <Input
                      @value={{String (divide this.cardFinalApprovedTotal 100)}}
                      disabled={{true}}
                      {{inputmask
                        alias="currency"
                        prefix="$"
                        unmaskAsNumber=true
                        digits="2"
                        autoUnmask=true
                      }}
                      data-test-card-final-approved-total-input
                    />
                  </div>

                  <hr class="my-2" />

                  <div class="grid grid-cols-2 gap-2 items-center">
                    <label class="font-semibold">
                      {{this.reimbursementPercentage}}% reimbursement of
                    </label>
                    <Input
                      @value={{String (divide (or this.prePercentageTotal 0) 100)}}
                      @onChange={{this.updateApprovedTotalFromPrePercentage}}
                      {{inputmask
                        alias="currency"
                        prefix="$"
                        unmaskAsNumber=true
                        digits="2"
                        autoUnmask=true
                      }}
                      @description="All other expenses"
                      data-test-pre-percentage-total
                    />

                    <label class="font-semibold">Approved Total</label>
                    <Input
                      @value={{String (divide this.approvedTotal 100)}}
                      @onChange={{this.updateApprovedTotal}}
                      disabled={{true}}
                      {{inputmask
                        alias="currency"
                        prefix="$"
                        unmaskAsNumber=true
                        digits="2"
                        autoUnmask=true
                      }}
                      data-test-approved-total-input
                    />
                  </div>
                {{else}}
                  {{! Non-Course Cash with percentage }}
                  <div class="grid grid-cols-2 gap-2 items-center">
                    <label class="font-semibold">
                      {{this.reimbursementPercentage}}% reimbursement of
                    </label>
                    <Input
                      @value={{String (divide (or this.prePercentageTotal 0) 100)}}
                      @onChange={{this.updateApprovedTotalFromPrePercentage}}
                      {{inputmask
                        alias="currency"
                        prefix="$"
                        unmaskAsNumber=true
                        digits="2"
                        autoUnmask=true
                      }}
                      data-test-pre-percentage-total
                    />

                    <label class="font-semibold">Approved Total</label>
                    <Input
                      @value={{String (divide this.approvedTotal 100)}}
                      @onChange={{this.updateApprovedTotal}}
                      disabled={{true}}
                      {{inputmask
                        alias="currency"
                        prefix="$"
                        unmaskAsNumber=true
                        digits="2"
                        autoUnmask=true
                      }}
                      data-test-approved-total-input
                    />
                  </div>
                {{/if}}
              {{else}}
                {{! Direct input (non-percentage) UI }}
                {{#if this.showCourseCashField}}
                  <Input
                    @label="Course Cash Amount"
                    @value={{String (divide (or this.cardFinalApprovedTotal 0) 100)}}
                    @onChange={{this.updateCardFinalApprovedTotal}}
                    {{inputmask
                      alias="currency"
                      prefix="$"
                      unmaskAsNumber=true
                      digits="2"
                      autoUnmask=true
                    }}
                    @description="Course Cash - Tuition"
                    data-test-card-final-approved-total-input
                  />
                {{/if}}

                {{#if this.showCourseCashField}}
                  <Input
                    @label="All Other Expenses"
                    @value={{String (divide this.approvedTotal 100)}}
                    @onChange={{this.updateApprovedTotal}}
                    {{inputmask
                      alias="currency"
                      prefix="$"
                      unmaskAsNumber=true
                      digits="2"
                      autoUnmask=true
                    }}
                    data-test-approved-total-input
                  />
                {{else}}
                  <Input
                    @value={{String (divide this.approvedTotal 100)}}
                    @label="Approved Total"
                    @onChange={{this.updateApprovedTotal}}
                    {{inputmask
                      alias="currency"
                      prefix="$"
                      unmaskAsNumber=true
                      digits="2"
                      autoUnmask=true
                    }}
                    data-test-approved-total-input
                  />
                {{/if}}
              {{/if}}
            </div>

            {{! Comment Section }}
            <div class="space-y-2">
              <h3 class="text-sm font-medium">Leave a Comment</h3>
              {{#if this.tasResponseNotes.length}}
                <SelectTextArea
                  @options={{this.tasResponseNotes}}
                  @optionLabel="title"
                  @optionValue="body"
                  @value={{this.comment}}
                  @onChange={{this.updateComment}}
                  @label="Select a canned response or write your own"
                  @hideLabel={{true}}
                  @selectPlaceholder="Select a canned response..."
                  @placeholder="Or write your own comment..."
                />
              {{else}}
                <Textarea
                  @value={{this.comment}}
                  @onInput={{this.updateComment}}
                  data-test-approve-comment
                />
              {{/if}}
              {{#unless this.comment}}
                <p class="text-xs text-red-600">
                  A comment is required before approving.
                </p>
              {{/unless}}
            </div>

            {{! Warnings Section }}
            {{#if this.employeeHasAppsInRepayment}}
              <TioErrorMessages
                @showErrors={{true}}
                @error="Please keep in mind that the employee has applications in repayment."
                class="text-sm"
                @icon="warning"
              />
            {{/if}}

            {{#if this.isTasParticipantInactive}}
              <TioErrorMessages
                @showErrors={{true}}
                @error="This employee is inactive in the TAS program."
                class="text-sm"
                @icon="warning"
              />
            {{/if}}
          </VStack>

          <m.Footer>
            <Footer
              @isRunning={{this.submitApprovalTask.isRunning}}
              @submitText="Approve"
              @cancelText="Cancel"
              @onSubmit={{this.attemptSubmitApproval}}
              @onCancel={{this.handleClose}}
              @submitDisabled={{or
                (not this.canSubmitApproval)
                Summary.isDisabledSubmitForApprover
              }}
            />
          </m.Footer>
        </LimitsSummary>

        {{! Out-of-sequence approver confirmation }}
        <TioConfirmAlert
          @show={{this.showConfirmApproverOverridePrompt}}
          @onConfirm={{this.submitApprovalTask.perform}}
          @onCancel={{this.cancelApproverOverride}}
          @confirmButtonText="Yes"
          @cancelButtonText="No"
          @disabled={{false}}
        >
          <p class="mb-2 font-semibold">
            You are not the expected approver for this application. Are you sure you want to proceed
            with this approval?
          </p>
        </TioConfirmAlert>
      </m.Body>
    </Modal>
  </template>
}

export default ApproveModal;
